// Hedef & Teşvik sihirbazı ortak yardımcıları. Kampanya modülünden kanıtlanmış
// helper'ları (lookup arama, biçimlendirme, CSV) yeniden kullanır.
import { authHeaders, getODataRows } from '../listPageStyles';
import {
  csvToArr, arrToCsv, fmtMoney, fmtDate, lookupLabel, resolveLabels,
  searchCustomers, searchCustomerGroups, searchProducts, searchProductGroups, searchVendors,
} from '../campaign/campaignUtils';

export {
  csvToArr, arrToCsv, fmtMoney, fmtDate, lookupLabel, resolveLabels,
  searchCustomers, searchCustomerGroups, searchProducts, searchProductGroups, searchVendors,
};

// Kademe matrisi JSON'unu güvenli parse et → [{pct,amount,gift}].
export function parseTiers(tiersJson) {
  if (!tiersJson) return [];
  try {
    const arr = JSON.parse(tiersJson);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

// ── Sabitler ──
export const PERIOD_TYPES = [
  { value: 'MONTH', label: 'Ay' },
  { value: 'QUARTER', label: 'Çeyrek' },
  { value: 'CUSTOM', label: 'Özel Aralık' },
];

export const METRIC_TYPES = [
  { value: 'AMOUNT', label: 'TL / Ciro', desc: 'Tutar bazlı hedef (₺)', icon: '₺' },
  { value: 'QUANTITY', label: 'Ürün Birimi', desc: 'Paket / Koli / Litre / Adet', icon: '▦' },
];

export const UNIT_OPTIONS = ['Paket', 'Koli', 'Litre', 'Adet', 'Kg'];

export const PRODUCT_SCOPE_TYPES = [
  { value: 'ALL', label: 'Tüm Ürünler', desc: 'Tüm SKU\'lar dahil', icon: '◎' },
  { value: 'GROUP', label: 'Ürün Grubu', desc: 'Kategori bazlı', icon: '⚇' },
  { value: 'SUBGROUP', label: 'Alt Grup', desc: 'Ek grup bazlı', icon: '☷' },
  { value: 'SKU', label: 'Tek / Çoklu Ürün', desc: 'Belirli SKU(lar)', icon: '☺' },
];

// Müşteri hedef türleri (kampanyadan birebir).
export const CUSTOMER_TARGET_TYPES = [
  { value: 'ALL', label: 'Tüm Müşteriler', desc: 'Tüm noktalar', icon: '◎' },
  { value: 'SINGLE', label: 'Tek Müşteri', desc: 'Belirli bir nokta', icon: '☺' },
  { value: 'MULTI', label: 'Çoklu Müşteri', desc: 'Seçili noktalar', icon: '☷' },
  { value: 'GROUP', label: 'Müşteri Grubu', desc: 'Segment / grup', icon: '⚇' },
];

// ── /Select uçları için küçük arama fabrikası (kampanya deseni, client filtreli) ──
// Sonsuz kaydırma için skip ile sayfa sayfa (SELECT_PAGE_SIZE) dilimlenir.
const SELECT_PAGE_SIZE = 20;
function makeSelectSearcher(endpoint) {
  let cache = null;
  return async function search(query, skip = 0) {
    if (!cache) {
      const r = await fetch(endpoint, { headers: authHeaders() });
      cache = r.ok ? await r.json() : [];
      if (!Array.isArray(cache)) cache = getODataRows(cache);
    }
    const term = String(query || '').toLocaleLowerCase('tr-TR');
    return cache
      .map((g) => ({ value: g.id || g.Id, label: lookupLabel(g) }))
      .filter((o) => o.value && (!term || o.label.toLocaleLowerCase('tr-TR').includes(term)))
      .slice(skip, skip + SELECT_PAGE_SIZE);
  };
}

export const searchRepresentatives = makeSelectSearcher('/api/representatives/Select');
export const searchRepresentativeGroups = makeSelectSearcher('/api/representativegroups/Select');
export const searchVendorGroups = makeSelectSearcher('/api/vendorgroups/Select');
export const searchProductSubGroups = makeSelectSearcher('/api/productsubgroups/Select');

// ── Dönem → ISO tarih aralığı ──
export function periodToDates({ periodType, month, quarter, year, startDate, endDate }) {
  const pad = (n) => String(n).padStart(2, '0');
  const iso = (y, m, d) => new Date(`${y}-${pad(m)}-${pad(d)}T00:00:00`).toISOString();
  const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m: 1-12
  if (periodType === 'MONTH' && month) {
    const [y, m] = month.split('-').map(Number);
    return { startDate: iso(y, m, 1), endDate: iso(y, m, lastDay(y, m)) };
  }
  if (periodType === 'QUARTER' && quarter && year) {
    const startM = (Number(quarter) - 1) * 3 + 1;
    const endM = startM + 2;
    return { startDate: iso(year, startM, 1), endDate: iso(year, endM, lastDay(year, endM)) };
  }
  if (periodType === 'CUSTOM') {
    return {
      startDate: startDate ? new Date(`${startDate}T00:00:00`).toISOString() : null,
      endDate: endDate ? new Date(`${endDate}T00:00:00`).toISOString() : null,
    };
  }
  return { startDate: null, endDate: null };
}

export function periodLabel({ periodType, month, quarter, year, startDate, endDate }) {
  if (periodType === 'MONTH' && month) {
    const [y, m] = month.split('-');
    const names = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    return `${names[Number(m) - 1]} ${y}`;
  }
  if (periodType === 'QUARTER' && quarter && year) return `${year} Ç${quarter}`;
  if (periodType === 'CUSTOM' && startDate && endDate) return `${startDate} → ${endDate}`;
  return '—';
}

export function itemsToCsv(items) { return arrToCsv((items || []).map((i) => i.value)); }
